import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, Building2, FileText, DollarSign, Clock } from "lucide-react";

function Nav() {
  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
      <div className="mx-auto max-w-6xl px-6 flex items-center justify-between h-16">
        <Logo />
        <nav className="hidden md:flex items-center gap-6 text-sm text-slate-600">
          <a href="#programs" className="hover:text-slate-900">Loan programs</a>
          <a href="#how" className="hover:text-slate-900">How it works</a>
          <a href="#faq" className="hover:text-slate-900">FAQ</a>
          <a href="#contact" className="hover:text-slate-900">Contact</a>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm text-slate-600 hover:text-slate-900" data-testid="nav-login-btn">Sign in</Link>
          <Button asChild size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="nav-apply-btn">
            <Link to="/register">Apply now</Link>
          </Button>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-6xl px-6 py-20 text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-blue-700 px-3 py-1 text-xs font-medium">
          <Check className="h-3.5 w-3.5" /> Trusted by 2,400+ businesses
        </div>
        <h1 className="mt-6 text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
          Business financing, made simple.
        </h1>
        <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
          Apply once. Get matched with the right loan program for your business.
          From $10,000 to $250 million. Decisions in days, not months.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700" data-testid="hero-apply-btn">
            <Link to="/register">Apply now <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
          <Button asChild size="lg" variant="outline" data-testid="hero-signin-btn">
            <Link to="/login">Sign in to your account</Link>
          </Button>
        </div>
        <div className="mt-6 text-sm text-slate-500">
          Free to apply · No impact to credit · Takes about 60 seconds
        </div>
      </div>
    </section>
  );
}

function Stats() {
  const items = [
    { k: "$4.2B", v: "Funded" },
    { k: "2,400+", v: "Businesses helped" },
    { k: "16", v: "Loan programs" },
    { k: "5–30 days", v: "Typical funding time" },
  ];
  return (
    <section className="bg-slate-50 border-y border-slate-200">
      <div className="mx-auto max-w-6xl px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
        {items.map((s) => (
          <div key={s.v} className="text-center">
            <div className="text-3xl font-bold text-slate-900">{s.k}</div>
            <div className="mt-1 text-sm text-slate-600">{s.v}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Programs({ products }) {
  return (
    <section id="programs" className="bg-white">
      <div className="mx-auto max-w-6xl px-6 py-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-slate-900">Loan programs</h2>
          <p className="mt-2 text-slate-600">Pick the one that fits your business.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p) => (
            <Link key={p.slug} to={`/programs/${p.slug}`} data-testid={`program-card-${p.slug}`}
              className="group block border border-slate-200 rounded-lg p-5 hover:border-blue-500 hover:shadow-md transition-all bg-white">
              <div className="text-xs font-medium text-blue-600 uppercase tracking-wide">{p.category}</div>
              <div className="mt-2 text-lg font-semibold text-slate-900 group-hover:text-blue-700">{p.name}</div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">{p.description}</p>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <span>${Math.round(p.min_amount / 1000)}K – ${(p.max_amount / 1_000_000).toFixed(0)}M</span>
                <span className="text-blue-600 group-hover:translate-x-0.5 transition-transform">Learn more →</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { icon: Building2, t: "Create your account", d: "Sign up in 60 seconds. Add your business details once — we save them for every application." },
    { icon: FileText, t: "Choose a loan program", d: "Pick from 16 programs. We show you exactly what documents you need for each." },
    { icon: DollarSign, t: "Get funded", d: "Upload documents, message us directly, and track your loan status in one place." },
  ];
  return (
    <section id="how" className="bg-slate-50 border-y border-slate-200">
      <div className="mx-auto max-w-6xl px-6 py-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-slate-900">How it works</h2>
          <p className="mt-2 text-slate-600">Three simple steps.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={s.t} className="bg-white rounded-lg border border-slate-200 p-6">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <s.icon className="h-5 w-5" />
              </div>
              <div className="mt-4 text-sm font-semibold text-slate-500">Step {i + 1}</div>
              <div className="mt-1 text-lg font-semibold text-slate-900">{s.t}</div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const faqs = [
    { q: "How much can I borrow?", a: "From $10,000 up to $250 million depending on the program and your business's financials." },
    { q: "How long does approval take?", a: "Working capital funds in 1-5 days. SBA and real estate loans typically take 30-60 days." },
    { q: "Is applying free?", a: "Yes. Applying is free and there's no impact to your credit to browse programs." },
    { q: "What documents will I need?", a: "It depends on the program, but usually bank statements, tax returns, and a business ID. Each program page lists the exact requirements." },
    { q: "Do you work with newer businesses?", a: "Yes. Some programs (like working capital) require only 6 months in business. SBA requires 2+ years." },
    { q: "Can I talk to a real person?", a: "Absolutely. Every application gets a dedicated loan officer you can message or call directly." },
  ];
  return (
    <section id="faq" className="bg-white">
      <div className="mx-auto max-w-4xl px-6 py-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-slate-900">Frequently asked questions</h2>
        </div>
        <div className="space-y-4">
          {faqs.map((f) => (
            <details key={f.q} className="group border border-slate-200 rounded-lg p-5 bg-white">
              <summary className="flex items-center justify-between cursor-pointer text-slate-900 font-medium">
                {f.q}
                <span className="text-blue-600 text-xl group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-slate-600 text-sm leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section id="contact" className="bg-blue-600 text-white">
      <div className="mx-auto max-w-6xl px-6 py-16 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold">Ready to apply?</h2>
        <p className="mt-3 text-blue-100 max-w-xl mx-auto">
          Create your account and start your application in minutes. Or sign in
          if you're already a customer.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button asChild size="lg" className="bg-white text-blue-700 hover:bg-blue-50" data-testid="cta-apply-btn">
            <Link to="/register">Apply now <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-blue-700" data-testid="cta-signin-btn">
            <Link to="/login">Sign in</Link>
          </Button>
        </div>
        <div className="mt-10 flex flex-col md:flex-row items-center justify-center gap-6 text-sm text-blue-100">
          <div className="flex items-center gap-2"><Clock className="h-4 w-4" /> Mon–Fri · 8am–7pm ET</div>
          <div>hello@venturenationals.com</div>
          <div><a href="tel:+19179452824" className="hover:text-white" data-testid="home-phone">(917) 945-2824</a></div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-slate-900 text-slate-400">
      <div className="mx-auto max-w-6xl px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
        <Logo variant="light" />
        <div>© 2026 Venture Nationals, Inc. All rights reserved.</div>
        <div className="flex gap-5">
          <a href="#" className="hover:text-white">Privacy</a>
          <a href="#" className="hover:text-white">Terms</a>
          <a href="#" className="hover:text-white">Contact</a>
        </div>
      </div>
    </footer>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => setProducts([])); }, []);
  return (
    <div className="min-h-screen bg-white">
      <Nav />
      <Hero />
      <Stats />
      <Programs products={products} />
      <HowItWorks />
      <FAQ />
      <CTA />
      <Footer />
    </div>
  );
}
